import PropTypes from 'prop-types';
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from "react-toastify";
import axios from 'axios';
import moment from 'moment';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Badge,
  Input,
} from "reactstrap";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';

const NewVehicleRegistrationDetails = props => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [vehicleDocuments, setVehicleDocuments] = useState([]);
  
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [selectedVehicleDocIds, setSelectedVehicleDocIds] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);

  useEffect(() => {
    fetchVehicleDetails();
  }, [vehicleId]);

  const openImagePreview = (imageUrl) => {
    setPreviewImage(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
    setShowImagePreview(false);
  };

  const getImageUrl = (doc) => {
    if (doc.document && doc.document !== '-') return doc.document;
    if (doc.document_media_path && doc.document_media_path !== '-') {
      if (doc.document_media_path.startsWith('http')) {
        return doc.document_media_path;
      }
      return `${process.env.REACT_APP_WEB_APP_URL}/${doc.document_media_path}`;
    }
    if (doc.document_media?.src) {
      if (doc.document_media.src.startsWith('http')) {
        return doc.document_media.src;
      }
      return `${process.env.REACT_APP_WEB_APP_URL}/${doc.document_media.src}`;
    }
    return '';
  };

  const fetchVehicleDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/new-vehicle-registration-details/${vehicleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        setVehicleInfo(response.data.data.vehicle_info);
        setVehicleDocuments(response.data.data.vehicle_documents || []);
      } else {
        toast.error('Failed to fetch vehicle details', {
          position: "top-right",
          autoClose: 3000,
        });
        navigate('/new-vehicle-registrations');
      }
    } catch (error) {
      console.error("Error fetching vehicle details:", error);
      toast.error('Failed to fetch vehicle details', {
        position: "top-right",
        autoClose: 3000,
      });
      navigate('/new-vehicle-registrations');
    } finally {
      setLoading(false);
    }
  };

  const toggleVehicleDocSelection = (docId) => {
    setSelectedVehicleDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleRemarkChange = (docId, value) => {
    setRemarks(prev => ({
      ...prev,
      [docId]: value
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved": return "success";
      case "rejected": return "danger";
      case "pending": return "warning";
      default: return "secondary";
    }
  };

  const isImage = (url) => {
    return /\.(jpeg|jpg|png|gif|bmp|webp)$/i.test(url);
  };

  const handleViewVehicleDocuments = () => {
    setSelectedVehicleDocIds([]);
    setVehicleModalOpen(true);
  };

  const handleApproveVehicleDocuments = async () => {
    if (selectedVehicleDocIds.length === 0) {
      toast.error('Please select at least one document.', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!vehicleInfo) {
      toast.error('Vehicle information not available', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const selectedRemarks = selectedVehicleDocIds.map(id => ({
      document_id: id,
      remark: remarks[id] || ""
    }));

    const payload = {
      action: 'approved',
      document_ids: selectedRemarks,
      userId: vehicleInfo.driver_id,
      vehicle_id: vehicleInfo.id
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/update-vehicles-documents`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Vehicle documents approved successfully', {
          position: "top-right",
          autoClose: 3000,
        });
        setVehicleModalOpen(false);
        fetchVehicleDetails();
      } else {
        toast.error('Failed to approve documents', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error approving vehicle documents:", error);
      toast.error('Failed to approve documents', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  document.title = "New Vehicle Registration Details | Moeen";
  
  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="New Vehicle Registration Details" />
          <Skeleton count={10} height={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="New Vehicle Registration Details" />
        
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <CardTitle className="h4">Vehicle Information</CardTitle>
                <Row className="mt-3">
                  <Col md="6">
                    <p><strong>License Plate:</strong> {vehicleInfo?.license_plate || 'N/A'}</p>
                    <p><strong>Make:</strong> {vehicleInfo?.make_name || 'N/A'}</p>
                    <p><strong>Model:</strong> {vehicleInfo?.model_name || 'N/A'}</p>
                    <p><strong>Body Type:</strong> {vehicleInfo?.body_type || 'N/A'}</p>
                    <p><strong>Year:</strong> {vehicleInfo?.year || 'N/A'}</p>
                    <p><strong>Color:</strong> {vehicleInfo?.color_name || 'N/A'}</p>
                  </Col>
                  <Col md="6">
                    <p><strong>VIN:</strong> {vehicleInfo?.vin || 'N/A'}</p>
                    <p><strong>Mileage:</strong> {vehicleInfo?.mileage || 'N/A'}</p>
                    <p><strong>Engine Type:</strong> {vehicleInfo?.engine_type || 'N/A'}</p>
                    <p><strong>Horse Power:</strong> {vehicleInfo?.horse_power || 'N/A'}</p>
                    <p><strong>Driver Name:</strong> {vehicleInfo?.driver_name || 'N/A'}</p>
                    <p><strong>Driver Email:</strong> {vehicleInfo?.driver_email || 'N/A'}</p>
                    <p><strong>Status:</strong> 
                      <Badge className={`ms-2 bg-${vehicleInfo?.in_service ? 'success' : 'danger'}`}>
                        {vehicleInfo?.in_service ? 'In Service' : 'Not In Service'}
                      </Badge>
                    </p>
                    <p><strong>Registration Date:</strong> {moment(vehicleInfo?.created_at).format('YYYY-MM-DD HH:mm')}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col lg="12">
            <Card>
              <CardBody>
                <CardTitle className="h4">Vehicle Documents</CardTitle>
                <p className="text-muted">Pending documents: {vehicleDocuments.length}</p>
                {vehicleDocuments.length > 0 ? (
                  <Button color="primary" onClick={handleViewVehicleDocuments}>
                    <i className="mdi mdi-eye"></i> View & Approve Documents
                  </Button>
                ) : (
                  <p className="text-success">All vehicle documents are approved</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Vehicle Documents Modal */}
        {vehicleModalOpen && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Vehicle Documents</h5>
                  <button type="button" className="btn-close" onClick={() => setVehicleModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex flex-wrap gap-3">
                    {vehicleDocuments.map(doc => {
                      const isApproved = doc.status?.toLowerCase() === 'approved';
                      const isSelected = selectedVehicleDocIds.includes(doc.id);
                      const docUrl = getImageUrl(doc);

                      return (
                        <div
                          key={doc.id}
                          className={`card p-2 ${isSelected ? 'border border-success' : ''} ${isApproved ? 'opacity-50' : ''}`}
                          style={{ width: "180px", position: "relative" }}
                        >
                          {isSelected && (
                            <div className="position-absolute top-0 end-0 m-1" style={{ zIndex: 10 }}>
                              <span className="badge bg-success">Selected</span>
                            </div>
                          )}
                          <Badge className={`mb-2 bg-${getStatusColor(doc.status)}`} style={{ zIndex: 10, position: "relative" }}>
                            {doc.status}
                          </Badge>
                          {docUrl && (
                            <div className="text-center" style={{ cursor: isImage(docUrl) ? 'pointer' : 'default' }}>
                              {isImage(docUrl) ? (
                                <img 
                                  src={docUrl} 
                                  alt="Document" 
                                  style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "4px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openImagePreview(docUrl);
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                      e.target.nextSibling.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <div 
                                className="d-flex align-items-center justify-content-center" 
                                style={{ 
                                  height: "120px", 
                                  background: "#f0f0f0",
                                  borderRadius: "4px",
                                  display: isImage(docUrl) ? 'none' : 'flex'
                                }}
                              >
                                <i className="mdi mdi-file-document" style={{ fontSize: "48px" }}></i>
                              </div>
                            </div>
                          )}
                          {!docUrl && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: "120px", background: "#f0f0f0", borderRadius: "4px" }}>
                              <i className="mdi mdi-file-document" style={{ fontSize: "48px" }}></i>
                            </div>
                          )}
                          <div className="mt-2">
                            <small className="text-muted">{doc.document_type?.document_type_name || doc.document_name || 'Document'}</small>
                          </div>
                          <div className="mt-2">
                            <Button
                              size="sm"
                              color={isSelected ? "danger" : "primary"}
                              style={{ width: "100%" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isApproved) {
                                  toggleVehicleDocSelection(doc.id);
                                }
                              }}
                              disabled={isApproved}
                            >
                              {isSelected ? "Deselect" : "Select"}
                            </Button>
                          </div>
                          {isSelected && !isApproved && (
                            <div className="mt-2">
                              <Input
                                type="textarea"
                                placeholder="Remarks (optional)"
                                value={remarks[doc.id] || ''}
                                onChange={(e) => handleRemarkChange(doc.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                rows="2"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="modal-footer">
                  <Button color="secondary" onClick={() => setVehicleModalOpen(false)}>Close</Button>
                  <Button color="success" onClick={handleApproveVehicleDocuments} disabled={selectedVehicleDocIds.length === 0}>
                    Approve Selected ({selectedVehicleDocIds.length})
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {showImagePreview && previewImage && (
          <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            role="dialog" 
            style={{ background: "rgba(0,0,0,0.9)", zIndex: 9999 }}
            onClick={closeImagePreview}
          >
            <div className="modal-dialog modal-xl" role="document" style={{ maxWidth: "90vw", margin: "auto" }}>
              <div className="modal-content" style={{ background: "transparent", border: "none" }}>
                <div className="modal-header" style={{ border: "none", justifyContent: "flex-end" }}>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={closeImagePreview}
                    style={{ fontSize: "2rem" }}
                  ></button>
                </div>
                <div className="modal-body d-flex align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
                  <img 
                    src={previewImage} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: "100%", 
                      maxHeight: "80vh", 
                      objectFit: "contain",
                      borderRadius: "8px"
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

NewVehicleRegistrationDetails.propTypes = {
  t: PropTypes.any
};

export default NewVehicleRegistrationDetails;



